import {
    useFrame,
    useThree,
} from '@react-three/fiber';
import {
    FramebufferTexture,
    LinearFilter,
    NoBlending,
    ShaderMaterial,
    Vector2,
} from 'three';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';
import {
    memo,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
} from 'react';
import {
    modelViewerPostEffectsAreActive,
    type ModelViewerPreviewPostEffectsData,
} from './config';
import { renderReadiness } from '../Rendering/browserReadiness';

interface Props {
    effects: ModelViewerPreviewPostEffectsData
    revision?: number
}

const PHOTO_EFFECTS_VERTEX_SHADER = `
    varying vec2 vUv;

    void main() {
        vUv = uv;
        gl_Position = vec4(position.xy, 0.0, 1.0);
    }
`;

const createFrameTexture = (width:number, height:number) => {
    const texture = new FramebufferTexture(width, height);
    texture.magFilter = LinearFilter;
    texture.minFilter = LinearFilter;
    texture.needsUpdate = true;
    return texture;
};

/**
 * The source texture is a copy of the already tone-mapped, sRGB framebuffer.
 * Keeping this pass display-referred preserves the current ACES result and,
 * more importantly, lets glass and floor reflections keep rendering through
 * their existing default-framebuffer path.
 */
const PHOTO_EFFECTS_FRAGMENT_SHADER = `
    uniform sampler2D sceneTexture;
    uniform vec2 texelSize;
    uniform float pixelRatio;
    uniform float diffusion;
    uniform float vignette;
    uniform float highlightRolloff;
    uniform float grain;

    varying vec2 vUv;

    float photoLuminance(vec3 color) {
        return dot(color, vec3(0.2126, 0.7152, 0.0722));
    }

    float photoNoise(vec2 point) {
        vec3 point3 = fract(vec3(point.xyx) * 0.1031);
        point3 += dot(point3, point3.yzx + 33.33);
        return fract((point3.x + point3.y) * point3.z);
    }

    vec3 photoHighlight(vec2 uv) {
        vec3 color = texture2D(
            sceneTexture,
            clamp(uv, vec2(0.0), vec2(1.0))
        ).rgb;
        float highlight = smoothstep(
            0.72,
            0.98,
            photoLuminance(color)
        );
        return color * highlight;
    }

    void main() {
        vec4 source = texture2D(sceneTexture, vUv);
        vec3 color = source.rgb;

        if(diffusion > 0.0001) {
            float radius = mix(4.0, 14.0, diffusion) * pixelRatio;
            vec2 horizontal = vec2(texelSize.x * radius, 0.0);
            vec2 vertical = vec2(0.0, texelSize.y * radius);
            vec2 diagonal = vec2(
                texelSize.x * radius,
                texelSize.y * radius
            );
            vec3 glow = photoHighlight(vUv) * 0.20;
            glow += photoHighlight(vUv + horizontal) * 0.12;
            glow += photoHighlight(vUv - horizontal) * 0.12;
            glow += photoHighlight(vUv + vertical) * 0.12;
            glow += photoHighlight(vUv - vertical) * 0.12;
            glow += photoHighlight(vUv + diagonal) * 0.08;
            glow += photoHighlight(vUv - diagonal) * 0.08;
            glow += photoHighlight(
                vUv + vec2(diagonal.x, -diagonal.y)
            ) * 0.08;
            glow += photoHighlight(
                vUv + vec2(-diagonal.x, diagonal.y)
            ) * 0.08;
            color += glow * diffusion * 0.55;
        }

        if(highlightRolloff > 0.0001) {
            float luminance = photoLuminance(color);
            float shoulderStart = 0.72;
            float excess = max(luminance - shoulderStart, 0.0);
            float rolledLuminance = luminance - excess + excess / (
                1.0 + 5.0 * highlightRolloff * excess
            );
            color *= rolledLuminance / max(luminance, 0.00001);
        }

        if(vignette > 0.0001) {
            vec2 centeredUv = vUv * 2.0 - 1.0;
            float vignetteMask = smoothstep(
                0.45,
                1.35,
                length(centeredUv)
            );
            color *= 1.0 - vignetteMask * vignette * 0.68;
        }

        if(grain > 0.0001) {
            vec2 outputPixel = floor(
                gl_FragCoord.xy / max(pixelRatio, 1.0)
            );
            float noise = photoNoise(outputPixel) - 0.5;
            float luminance = clamp(photoLuminance(color), 0.0, 1.0);
            float midtone = 1.0 - abs(luminance - 0.5) * 2.0;
            float amplitude = grain * (0.008 + grain * 0.042);
            color += vec3(
                noise * amplitude * mix(0.6, 1.0, midtone)
            );
        }

        // The completed preview is an opaque photo. Re-emitting the copied
        // framebuffer alpha would make transparent product materials composite
        // over the page a second time, washing their color toward white.
        gl_FragColor = vec4(clamp(color, 0.0, 1.0), 1.0);
    }
`;

/**
 * Lightweight final-frame treatment for product renders.
 *
 * A priority-one frame callback first performs the normal scene render to the
 * canvas. That completed LDR frame is copied into one RGBA8 texture, then a
 * fullscreen shader applies every effect in one pass. Besides avoiding a
 * multi-target composer at 4x supersampling, this order preserves the
 * default-framebuffer checks used by Studio reflections and glass.
 */
export default memo(function Effects({effects, revision}:Props) {
    const {
        camera,
        gl,
        invalidate,
        scene,
    } = useThree();
    const copyOrigin = useMemo(() => new Vector2(), []);
    const drawingBufferSize = useMemo(() => new Vector2(), []);
    const initialFrameTexture = useMemo(
        () => createFrameTexture(1, 1),
        [],
    );
    const frameTextureRef = useRef(initialFrameTexture);
    const material = useMemo(() => new ShaderMaterial({
        blending: NoBlending,
        depthTest: false,
        depthWrite: false,
        fragmentShader: PHOTO_EFFECTS_FRAGMENT_SHADER,
        toneMapped: false,
        uniforms: {
            sceneTexture: {value: initialFrameTexture},
            texelSize: {value: new Vector2(1, 1)},
            pixelRatio: {value: 1},
            diffusion: {value: effects.diffusion},
            vignette: {value: effects.vignette},
            highlightRolloff: {value: effects.highlight_rolloff},
            grain: {value: effects.grain},
        },
        vertexShader: PHOTO_EFFECTS_VERTEX_SHADER,
    }), [initialFrameTexture]);
    const fullscreenQuad = useMemo(
        () => new FullScreenQuad(material),
        [material],
    );
    const pendingRevision = useRef<number>();
    const pendingRevisionFrames = useRef(0);
    const effectsActive = modelViewerPostEffectsAreActive(effects);

    useLayoutEffect(() => {
        material.uniforms.diffusion.value = effects.diffusion;
        material.uniforms.vignette.value = effects.vignette;
        material.uniforms.highlightRolloff.value = (
            effects.highlight_rolloff
        );
        material.uniforms.grain.value = effects.grain;

        if(revision !== undefined) {
            renderReadiness.reset('postEffects', revision);
            pendingRevision.current = revision;
            pendingRevisionFrames.current = 2;
        }
        invalidate();
    }, [
        effects.diffusion,
        effects.grain,
        effects.highlight_rolloff,
        effects.vignette,
        invalidate,
        material,
        revision,
    ]);

    const acknowledgeRenderedRevision = () => {
        if(pendingRevision.current === undefined) return;

        pendingRevisionFrames.current -= 1;
        if(pendingRevisionFrames.current > 0) {
            invalidate();
            return;
        }

        renderReadiness.acknowledge(
            'postEffects',
            pendingRevision.current,
        );
        pendingRevision.current = undefined;
    };

    useFrame(() => {
        const previousRenderTarget = gl.getRenderTarget();
        try {
            // Preserve the existing main-render semantics before copying the
            // completed photo for the fullscreen treatment.
            gl.setRenderTarget(null);
            gl.render(scene, camera);

            if(!effectsActive) {
                acknowledgeRenderedRevision();
                return;
            }

            gl.getDrawingBufferSize(drawingBufferSize);
            const width = Math.max(1, Math.round(drawingBufferSize.x));
            const height = Math.max(1, Math.round(drawingBufferSize.y));
            let frameTexture = frameTextureRef.current;
            const textureImage = frameTexture.image as {
                height: number
                width: number
            };
            if(
                textureImage.width !== width
                || textureImage.height !== height
            ) {
                // WebGLTextures does not reallocate an uploaded
                // FramebufferTexture when only image dimensions change.
                // Replace it so switching supersampling/resolution cannot
                // retain quadrants from the previous GPU allocation.
                const replacement = createFrameTexture(width, height);
                frameTexture.dispose();
                frameTexture = replacement;
                frameTextureRef.current = replacement;
                material.uniforms.sceneTexture.value = replacement;
                (
                    material.uniforms.texelSize.value as Vector2
                ).set(1 / width, 1 / height);
            }
            material.uniforms.pixelRatio.value = gl.getPixelRatio();
            gl.copyFramebufferToTexture(copyOrigin, frameTexture);

            gl.setRenderTarget(null);
            fullscreenQuad.render(gl);

            // A second demand-loop frame catches camera, transmission, and
            // reflection changes that settle during the first scene pass.
            acknowledgeRenderedRevision();
        } finally {
            gl.setRenderTarget(previousRenderTarget);
        }
    }, 1);

    useEffect(() => {
        return () => {
            material.dispose();
            frameTextureRef.current.dispose();
        };
    }, [material]);

    return null;
});
